import { useState, useEffect } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, MoreHorizontal } from "lucide-react";
import type { EmailCampaignReportContext, ReportData } from "./index";

type EmailPreview = {
  to: string;
  subject: string;
  html: string;
};

function summarizeEmailReport(reportData: ReportData[]) {
  let opens = 0;
  let clicks = 0;

  for (const row of reportData) {
    if (row.numOpens > 0) opens++;
    if (row.numClicks > 0) clicks++;
  }

  const ctor = opens > 0 ? (clicks / opens) * 100 : 0;
  const openRate = reportData.length > 0 ? (opens / reportData.length) * 100 : 0;

  return { sends: reportData.length, opens, clicks, ctor, openRate };
}

function formatCtor(ctor: number) {
  return `${ctor.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

export function EmailCampaignReportReportTab() {
  const { id } = useParams<{ id: string }>();
  const { reportData } = useOutletContext<EmailCampaignReportContext>();
  const [previewEmail, setPreviewEmail] = useState<string | null>(null);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  useEffect(() => {
    async function fetchPreview() {
      if (!id || !previewEmail) return;

      setIsPreviewLoading(true);
      setPreviewError(null);
      setPreview(null);

      try {
        const response = await fetch(
          `/api/email-campaigns/${id}/preview?email=${encodeURIComponent(previewEmail)}`,
        );
        const data = (await response.json().catch(() => ({}))) as {
          preview?: EmailPreview;
          error?: string;
        };

        if (!response.ok) {
          setPreviewError(typeof data.error === "string" ? data.error : "Could not load preview");
          return;
        }

        if (!data.preview?.html || !data.preview.subject) {
          setPreviewError("Preview response was incomplete");
          return;
        }

        setPreview(data.preview);
      } catch (error) {
        console.error("Failed to fetch email preview:", error);
        setPreviewError("Could not load preview");
      } finally {
        setIsPreviewLoading(false);
      }
    }

    void fetchPreview();
  }, [id, previewEmail]);

  const handlePreviewOpenChange = (open: boolean) => {
    if (!open) {
      setPreviewEmail(null);
      setPreview(null);
      setPreviewError(null);
    }
  };

  const { sends, opens, clicks, ctor, openRate } = summarizeEmailReport(reportData);

  return (
    <>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="gap-0 py-4 shadow-sm">
          <CardContent className="space-y-1 pt-0">
            <p className="text-sm text-muted-foreground">Sends</p>
            <p className="text-2xl font-semibold tabular-nums">{sends}</p>
          </CardContent>
        </Card>
        <Card className="gap-0 py-4 shadow-sm">
          <CardContent className="space-y-1 pt-0">
            <p className="text-sm text-muted-foreground">Opens</p>
            <p className="text-2xl font-semibold tabular-nums">
              {opens}{" "}
              <span className="text-base font-normal text-muted-foreground">
                ({formatCtor(openRate)})
              </span>
            </p>
          </CardContent>
        </Card>
        <Card className="gap-0 py-4 shadow-sm">
          <CardContent className="space-y-1 pt-0">
            <p className="text-sm text-muted-foreground">Clicks</p>
            <p className="text-2xl font-semibold tabular-nums">
              {clicks}{" "}
              <span className="text-base font-normal text-muted-foreground">
                ({formatCtor(ctor)})
              </span>
            </p>
          </CardContent>
        </Card>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Opens</TableHead>
            <TableHead>Clicks</TableHead>
            <TableHead className="w-[60px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reportData && reportData.length > 0 ? (
            reportData.map((row) => (
              <TableRow key={row.email}>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.numOpens}</TableCell>
                <TableCell>{row.numClicks}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setPreviewEmail(row.email)}>
                        Preview Email
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="h-24 text-center">
                No email addresses found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={previewEmail != null} onOpenChange={handlePreviewOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email preview</DialogTitle>
          </DialogHeader>

          {isPreviewLoading ? (
            <div className="flex justify-center items-center min-h-[320px]">
              <Loader2 className="animate-spin h-10 w-10 text-muted-foreground" />
            </div>
          ) : previewError ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
              <p className="text-sm text-destructive">{previewError}</p>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>To</Label>
                <p className="text-sm">{preview.to}</p>
              </div>
              <div className="space-y-2">
                <Label>Subject</Label>
                <p className="text-sm font-medium">{preview.subject}</p>
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <div className="rounded-lg border overflow-hidden bg-white">
                  <iframe
                    title="Email preview"
                    srcDoc={preview.html}
                    className="w-full min-h-[480px] border-0"
                    sandbox=""
                  />
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
